import React, { useEffect, useState, useRef } from 'react';
import { EventBus } from '../../../shared/EventBus';
import type { SimLiveDigest } from '../../../shared/sim/SimLiveDigest';
import { bootSearchParams } from '../../../config/boot/bootUrl';

/**
 * SimLiveHUD — top-right overlay that mirrors the live `simLive`
 * broadcast (sim/H) for any client connected to a room running a sim.
 *
 * Mount story: always rendered into the React tree, but `null`-returns
 * until the first `net:message:simLive` lands. Once a digest arrives,
 * the panel renders top damage / top kills / top abilities and updates
 * once per server tick (~1Hz). After 2 seconds with no new digests
 * (match ended on the server side, broadcast stopped) the panel
 * collapses into a "Match Result" card showing winner + top damage,
 * which then auto-fades 5 seconds later.
 *
 * Suppressing the HUD: `?simHud=0` URL flag — for clean OBS captures
 * by streaming-camera spectators who want only the world view.
 *
 * Subscription path: NetworkManager forwards the `simLive` wire message
 * to `EventBus.emit('net:message:simLive', digest)`. We listen on that
 * topic instead of binding to the Colyseus room directly — keeps the
 * component framework-agnostic and consistent with `chatMessage`.
 */

const STALE_AFTER_MS = 2000;
const RESULT_HOLD_MS = 5000;

function shouldEnable(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = bootSearchParams();
    if (params.get('simHud') === '0') return false;
  } catch { /* ignore */ }
  return true;
}

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface ViewState {
  /** Latest digest the server pushed. */
  digest: SimLiveDigest | null;
  /** Wall-clock ms when the latest digest arrived. */
  receivedAt: number;
  /** Set once a stop is detected; held for `RESULT_HOLD_MS` then HUD unmounts. */
  endedAt: number | null;
}

const SimLiveHUDPanel: React.FC = () => {
  const [view, setView] = useState<ViewState>({ digest: null, receivedAt: 0, endedAt: null });
  // staleCheckTickRef: forces a re-render every 500ms so the elapsed
  // counter ticks and the stale-after detection fires without depending
  // on the next digest arriving.
  const [, setTick] = useState(0);
  const tickRef = useRef<NodeJS.Timeout | null>(null);
  const endedAtRef = useRef<number | null>(null);
  endedAtRef.current = view.endedAt;

  useEffect(() => {
    const bus = EventBus.getInstance();
    const onDigest = (digest: SimLiveDigest) => {
      setView({ digest, receivedAt: Date.now(), endedAt: null });
    };
    bus.on('net:message:simLive', onDigest as any);
    return () => {
      bus.off('net:message:simLive', onDigest as any);
    };
  }, []);

  // Only while a digest shows (B7: the always-on tick re-rendered the hidden
  // panel twice a second all game long). It stops once the result card's
  // hold has passed; a new digest starts it again.
  const showing = view.digest !== null;
  useEffect(() => {
    if (!showing) return undefined;
    tickRef.current = setInterval(() => {
      setTick((n) => n + 1);
      if (endedAtRef.current !== null && Date.now() - endedAtRef.current >= RESULT_HOLD_MS) {
        if (tickRef.current) clearInterval(tickRef.current);
        tickRef.current = null;
      }
    }, 500);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
    };
  }, [showing, view.receivedAt]);

  // Detect end-of-match (no fresh digests arriving). Promote view to
  // "result card" mode; auto-unmount after RESULT_HOLD_MS.
  useEffect(() => {
    if (!view.digest || view.endedAt !== null) return;
    const since = Date.now() - view.receivedAt;
    if (since >= STALE_AFTER_MS) {
      setView((prev) => ({ ...prev, endedAt: Date.now() }));
    }
  });

  // Nothing to show yet, or held past the result window.
  if (!view.digest) return null;
  if (view.endedAt !== null && Date.now() - view.endedAt >= RESULT_HOLD_MS) {
    return null;
  }

  const d = view.digest;
  const isResult = view.endedAt !== null;
  const headerLabel = isResult ? 'SIM RESULT' : 'SIM';
  const elapsed = isResult
    ? formatElapsed(d.elapsedMs)
    : formatElapsed(Math.max(d.elapsedMs, Date.now() - view.receivedAt + d.elapsedMs));

  return (
    <div
      data-testid="sim-live-hud"
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9997,
        background: 'rgba(8, 8, 15, 0.92)',
        color: '#e8e8f0',
        fontFamily: 'monospace',
        fontSize: 11,
        lineHeight: 1.4,
        padding: '8px 12px',
        border: `1px solid ${isResult ? '#ffd700' : '#3a4256'}`,
        borderRadius: 4,
        minWidth: 240,
        maxWidth: 320,
        pointerEvents: 'none',
        userSelect: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
      }}
    >
      <div style={{ color: isResult ? '#ffd700' : '#7af', marginBottom: 4, letterSpacing: '0.05em', fontWeight: 600 }}>
        {headerLabel} · {elapsed} · {d.aliveCount}/{d.totalParticipants} alive
      </div>

      <Section title="Top Damage" rows={d.topDamage} renderRow={(r) => (
        <Row
          key={r.heroName}
          name={r.heroName}
          klass={r.heroClass}
          metric={r.damageDealt.toLocaleString()}
          alive={r.alive}
        />
      )} />

      <Section title="Top Kills" rows={d.topKills.filter((r) => r.kills > 0)} renderRow={(r, i) => (
        <Row
          key={r.heroName}
          name={r.heroName}
          klass={r.heroClass}
          metric={`${r.kills}${i === 0 ? ' ★' : ''}`}
          alive={r.alive}
        />
      )} />

      <Section title="Top Abilities" rows={d.topAbilities} renderRow={(r) => (
        <Row
          key={r.abilityName}
          name={r.abilityName}
          klass=""
          metric={`${r.casts} / ${r.avgDamagePerHit.toFixed(0)}`}
          alive
        />
      )} />
    </div>
  );
};

interface SectionProps<T> {
  title: string;
  rows: T[];
  renderRow: (row: T, index: number) => React.ReactNode;
}

function Section<T>({ title, rows, renderRow }: SectionProps<T>): React.ReactElement {
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ color: '#7af', fontSize: 9, letterSpacing: '0.08em', marginBottom: 2 }}>
        {title.toUpperCase()}
      </div>
      {rows.length === 0 ? (
        <div style={{ color: '#666', fontStyle: 'italic' }}>—</div>
      ) : (
        rows.map((r, i) => renderRow(r, i))
      )}
    </div>
  );
}

interface RowProps {
  name: string;
  klass: string;
  metric: string;
  alive: boolean;
}

const Row: React.FC<RowProps> = ({ name, klass, metric, alive }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 8,
      opacity: alive ? 1 : 0.45,
      textDecoration: alive ? 'none' : 'line-through',
    }}
  >
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {name}
      {klass ? <span style={{ color: '#888', marginLeft: 4 }}>({klass.slice(0, 3)})</span> : null}
    </span>
    <span style={{ color: '#ddd' }}>{metric}</span>
  </div>
);

export const SimLiveHUD: React.FC = () => {
  if (!shouldEnable()) return null;
  return <SimLiveHUDPanel />;
};
